import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { AppContext } from "../AppContext";

const RegisterPage = () => {
  const { register } = useContext(AppContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [registrationSuccessful, setRegistrationSuccessful] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    if (password !== confirmPassword) {
      setError("Mật khẩu xác nhận không khớp.");
      setLoading(false);
      return;
    }

    try {
      await register(email, password);
      // Logic sau khi đăng ký thành công
      setSuccessMessage(
        `Đăng ký thành công! Vui lòng kiểm tra hộp thư "${email}" để xác minh tài khoản, kiểm tra mục Spam/Rác nếu không tìm thấy.`
      );
      setRegistrationSuccessful(true);
      setEmail("");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      // Logic xử lý lỗi
      let errorMessage = "Đăng ký thất bại. Vui lòng thử lại.";
      switch (err.code) {
        case "auth/email-already-in-use":
          errorMessage = "Email đã được sử dụng.";
          break;
        case "auth/weak-password":
          errorMessage = "Mật khẩu quá yếu. Mật khẩu nên có ít nhất 6 ký tự.";
          break;
        case "auth/invalid-email":
          errorMessage = "Email không hợp lệ.";
          break;
        case "auth/too-many-requests":
          errorMessage = "Quá nhiều yêu cầu đăng ký. Vui lòng thử lại sau.";
          break;
        default:
          errorMessage = err.message || errorMessage;
          break;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-160px)] bg-gray-100 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md animate-fade-in-down">
        <h2 className="text-4xl font-extrabold text-gray-900 text-center mb-8">
          Đăng Ký
        </h2>

        {registrationSuccessful ? (
          <p className="text-green-700 text-lg text-center mb-4">
            {successMessage}
          </p>
        ) : (
          <>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                <strong className="font-bold">Lỗi!</strong>
                <span className="block sm:inline"> {error}</span>
              </div>
            )}

            <form onSubmit={handleRegister}>
              <InputField
                id="email"
                label="Email"
                type="email"
                icon={<Mail size={20} />}
                value={email}
                onChange={setEmail}
                placeholder="Nhập email"
                disabled={loading}
              />
              <InputField
                id="password"
                label="Mật khẩu"
                type="password"
                icon={<Lock size={20} />}
                value={password}
                onChange={setPassword}
                placeholder="Nhập mật khẩu"
                disabled={loading}
              />
              <InputField
                id="confirm-password"
                label="Xác nhận mật khẩu"
                type="password"
                icon={<Lock size={20} />}
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Xác nhận mật khẩu"
                disabled={loading}
              />

              <button
                type="submit"
                className={`bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-full w-full transition-all duration-300 transform hover:scale-105 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                Đăng Ký
              </button>
            </form>

            <button
              onClick={() => navigate("/login")}
              className="text-blue-600 hover:underline bg-transparent border-none p-0 cursor-pointer text-sm mt-6"
              disabled={loading}
            >
              Đã có tài khoản? Đăng nhập ngay
            </button>
          </>
        )}
      </div>

      <style>
        {`
          @keyframes fadeInDown {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in-down {
            animation: fadeInDown 0.5s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
};

const InputField = ({
  id,
  label,
  type,
  icon,
  value,
  onChange,
  placeholder,
  disabled,
}) => (
  <div className="mb-6">
    <label htmlFor={id} className="block text-gray-700 text-sm font-bold mb-2">
      {label}
    </label>
    <div className="relative">
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
        {icon}
      </div>
      <input
        type={type}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="shadow appearance-none border rounded-xl w-full py-3 px-10 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
        required
        disabled={disabled}
      />
    </div>
  </div>
);

export default RegisterPage;
